<template>
	<v-dialog :value="value" scrollable max-width="1800px" persistent>
		<v-card height="90vh" width>
			<v-toolbar flat>
				<v-toolbar-title> <v-icon left>mdi-upload</v-icon>Bulk Upload </v-toolbar-title>
				<v-spacer></v-spacer>

				<v-btn icon flat @click.stop="close()">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>

			<v-divider></v-divider>
			<div
				v-if="state !== BulkOperationState.waitingForFiles"
				style="
					position: absolute;
					top: 65px;
					bottom: 0px;
					height: calc(100% - 65px);
					width: 350px;
					border-right: rgba(0, 0, 0, 0.12) solid thin;
				"
			>
				<bulk-upload-options
					v-if="state === BulkOperationState.userInput"
					:files_extension="files_extension"
					:allowed_types="allowed_types"
					:approve.sync="upload_options.approve"
					:contributors.sync="upload_options.contributors"
					:destination_folder.sync="upload_options.destination_folder"
					:type.sync="upload_options.type"
					:workflow.sync="upload_options.workflow"
				/>
				<div class="pl-3 pr-3" v-if="state === BulkOperationState.userInput && uploadMethod === DRAG_AND_DROP_OPTION">
					<v-divider></v-divider>
					<v-subheader>Tools</v-subheader>
					<bulk-upload-replace-dialog @input="quickReplace" />
					<v-btn block small color="green darken-3" outline @click="downloadXlsx">
						<v-icon left>mdi-download</v-icon>Download XLSX
					</v-btn>
					<v-btn block small color="green darken-3" outline @click="uploadXlsx">
						<v-icon left>mdi-upload</v-icon>Upload XLSX
					</v-btn>
				</div>
				<div
					class="pl-3 pr-3"
					v-if="
						state === BulkOperationState.userInput &&
						uploadMethod === XLS_OPTION &&
						contentBulkUploadOptions.attachments
					"
				>
					<v-tooltip bottom>
						<template v-slot:activator="{ on }">
							<v-btn v-on="on" block color="green darken-3" outline @click="uploadContentAttachments">
								<v-icon left>mdi-upload</v-icon>Upload Attachments
							</v-btn>
						</template>
						<span>The image filenames should match the content custom_id where it will be attached</span>
					</v-tooltip>
				</div>
				<div style="position: absolute; bottom: 0px; padding: 10px; width: 100%">
					<v-divider></v-divider>
					<v-btn
						color="success"
						block
						outline
						@click.stop="uploadContent"
						v-if="state === BulkOperationState.userInput"
					>
						<v-icon left>mdi-play</v-icon>Start Upload
					</v-btn>
					<v-btn
						color="red"
						block
						outline
						@click.stop="state = BulkOperationState.cancelled"
						v-if="state === BulkOperationState.uploading"
					>
						<v-icon left>mdi-stop</v-icon>Cancel Upload
					</v-btn>
					<v-btn color="primary" block outline @click.stop="close()" v-if="state === BulkOperationState.completed">
						<v-icon left>mdi-close</v-icon>Close
					</v-btn>
				</div>
				<bulk-upload-progress
					v-if="state === BulkOperationState.completed || state === BulkOperationState.uploading"
					:items="new_contents"
				/>
			</div>
			<bulk-upload-table
				v-if="state !== BulkOperationState.waitingForFiles"
				:contents="new_contents"
				:disabled="state !== BulkOperationState.userInput"
				:type="content_type"
			/>
			<template v-if="state === BulkOperationState.waitingForFiles">
				<bulk-upload-drag-and-drop
					v-if="uploadMethod === DRAG_AND_DROP_OPTION"
					:type="content_type"
					@drop-finished="manageFiles"
				/>
				<bulk-upload-upload-xls
					v-if="uploadMethod === XLS_OPTION"
					:type="content_type"
					@upload-finished="manageFiles"
				/>
				<bulk-upload-upload-xls-with-reference
					v-if="uploadMethod === XLS_REFERENCE_OPTION"
					@upload-finished="manageFiles"
					:type="content_type"
				/>
			</template>
		</v-card>
	</v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { STORE_USER_NAME, STATE_PROFILE } from '@/store_constants/user';
import { selectFileFromComputer } from '@/utils/bulkUpload';

import { Attachment } from '@/classes/content/index.js';
import { DRAG_AND_DROP_OPTION, XLS_OPTION, XLS_REFERENCE_OPTION } from './constants.js';

import PtApiClient from 'plant-api-client';

import BulkUploadReplaceDialog from './ReplaceDialog.vue';
import BulkUploadTable from './Table.vue';
import BulkUploadOptions from './Options.vue';
import BulkUploadProgress from './Progress.vue';
import BulkUploadDragAndDrop from './DragAndDrop';
import BulkUploadUploadXls from './UploadXls';
import BulkUploadUploadXlsWithReference from './UploadXlsWithReference';

import { STORE_EXPLORER_NAME, STATE_FOLDER, ACTION_OPEN_FOLDER } from '@/store_constants/explorer';
import { STORE_NOTIFICATIONS_NAME, ACTION_NOTIFICATIONS_WARNING } from '@/store_constants/notifications';

import { BulkOperationState } from 'plant-common/src/models/bulkOperationState';
import { ContentOperationStatus } from 'plant-common/src/models/contentOperationStatus';
import { isValidCustomId } from '@/utils/customIdFunctions';
import { downloadFileTemplate, uploadFileSpreadSheet } from '@/utils/spreadSheetFunctions';
import { getContentBulkUploadOptions, getContentUploadMethod } from '@/classes/content/contentBodyFunctions';

export default {
	name: 'BulkUpload',
	props: ['value', 'content_type'],
	components: {
		BulkUploadReplaceDialog,
		BulkUploadTable,
		BulkUploadOptions,
		BulkUploadProgress,
		BulkUploadDragAndDrop,
		BulkUploadUploadXls,
		BulkUploadUploadXlsWithReference
	},
	data() {
		return {
			BulkOperationState,
			state: BulkOperationState.waitingForFiles,
			files_extension: '',
			allowed_types: [],
			upload_options: {
				destination_folder: { _id: '', name: '' },
				type: '',
				workflow: 'no_reviewer',
				approve: false,
				contributors: [{ role: 'author', user_id: '' }]
			},
			new_contents: [],
			files: [],
			DRAG_AND_DROP_OPTION,
			XLS_OPTION,
			XLS_REFERENCE_OPTION
		};
	},
	computed: {
		...mapState(STORE_EXPLORER_NAME, {
			folder: STATE_FOLDER
		}),
		...mapState(STORE_USER_NAME, {
			profile: STATE_PROFILE
		}),
		uploadMethod() {
			return getContentUploadMethod(this.content_type);
		},
		contentBulkUploadOptions() {
			return getContentBulkUploadOptions(this.content_type);
		}
	},
	created() {
		this.setDefaultOptions();
	},
	methods: {
		...mapActions(STORE_EXPLORER_NAME, {
			refreshFolder: ACTION_OPEN_FOLDER
		}),
		...mapActions(STORE_NOTIFICATIONS_NAME, {
			warning: ACTION_NOTIFICATIONS_WARNING
		}),
		setDefaultOptions() {
			this.upload_options.contributors[0].user_id = this.profile._id;
			this.upload_options.destination_folder._id = this.folder._id;
			this.upload_options.destination_folder.name = this.folder.name;
			this.upload_options.workflow = 'no_reviewer';
		},
		manageFiles(newContents) {
			this.new_contents = newContents;
			this.upload_options.type = this.content_type;
			this.allowed_types = [this.content_type];
			this.state = BulkOperationState.userInput;
		},
		convertHashToNumber(replace, index) {
			let replacement = replace;
			const hashes = replace.match(/#{1,6}/g);

			if (hashes) {
				const paddedIndex = index.toString().padStart(hashes[0].length, '0');
				replacement = replace.replace(/#{1,6}/g, paddedIndex);
			}

			return replacement;
		},
		quickReplace({ field, find, replace, start }) {
			let currentIndex = start;

			for (const content of this.new_contents) {
				const replacement = this.convertHashToNumber(replace, currentIndex);
				content[field] = content[field].replace(find || /.*/, replacement);
				currentIndex = currentIndex + 1;
			}
		},
		downloadXlsx() {
			downloadFileTemplate(this.new_contents);
		},
		async uploadXlsx() {
			await uploadFileSpreadSheet(this.new_contents);
		},
		async uploadContentAttachments() {
			const attachmentsConfig = this.contentBulkUploadOptions.attachments[0];

			const files = await this.getAttachmentsFiles(attachmentsConfig);
			const customIdsNotFound = this.attachFilesToContent(files, attachmentsConfig);

			if (customIdsNotFound.length > 0) this.showCustomIdsNotFoundWarning(customIdsNotFound);
		},
		async getAttachmentsFiles(attachmentsConfig) {
			const files = await selectFileFromComputer(attachmentsConfig.accept, true);

			return files || [];
		},
		attachFilesToContent(files, attachmentsConfig) {
			const customIdsNotFound = [];

			for (const file of files) {
				const { extension, filename } = this.getFilenameAndExtensionFrom(file);
				const content = this.new_contents.find(content => content.custom_id === filename);

				if (!content) {
					customIdsNotFound.push(filename);
					continue;
				}

				const url = URL.createObjectURL(file);
				const attachment = new Attachment(attachmentsConfig.name, file, url, extension);

				content.body.attachments.push(attachment);
			}

			return customIdsNotFound;
		},
		getFilenameAndExtensionFrom(file) {
			const name = file.name;
			const dotExtensionIndex = name.lastIndexOf('.');
			const hasExtension = dotExtensionIndex !== -1;

			return {
				filename: hasExtension ? name.slice(0, dotExtensionIndex) : name,
				extension: hasExtension ? name.slice(dotExtensionIndex) : ''
			};
		},
		showCustomIdsNotFoundWarning(customIds) {
			this.warning({
				message: `The following custom_ids were not found in current list: ${customIds.join(', ')}`,
				timeout: -1
			});
		},
		async uploadContent() {
			this.state = BulkOperationState.uploading;

			for (const content of this.new_contents) {
				if (this.state === BulkOperationState.cancelled) {
					content.status = ContentOperationStatus.error;
					continue;
				}

				if (!isValidCustomId(content.custom_id)) {
					content.status = ContentOperationStatus.error;
					content.error = 'The custom id must not contain special characters.';
					continue;
				}

				await this.processContent(content);
			}

			this.state = BulkOperationState.completed;
		},
		async processContent(content) {
			content.status = ContentOperationStatus.inProgress;

			const contentCreated = await this.createContent(content);
			if (!contentCreated) return;

			const content_id = this.getContentIdFrom(contentCreated);
			content.uploaded = contentCreated;

			const workflowChanged = await this.changeWorkflow(content_id, content);
			if (!workflowChanged) return;

			const userRoleAssigned = await this.assignUserRole(content_id, content);
			if (!userRoleAssigned) return;

			const revisionBodyUpdated = await this.updateRevisionBody(content_id, content);
			if (!revisionBodyUpdated) return;

			const revisionMetadataUpdated = await this.updateRevisionMetadata(content_id, content);
			if (!revisionMetadataUpdated) return;

			if (this.upload_options.approve) {
				const revisionApproved = await this.approveRevision(content_id, content);
				if (!revisionApproved) return;
			}

			content.status = ContentOperationStatus.done;
		},
		async createContent(content) {
			try {
				return await PtApiClient.createContent(
					content.title,
					content.custom_id,
					this.upload_options.destination_folder._id,
					this.upload_options.type
				);
			} catch (error) {
				this.handleStepError(content, error);
				return false;
			}
		},
		async changeWorkflow(content_id, content) {
			try {
				await PtApiClient.updateDraftWorkflow(content_id, 'method', 'changeWorkflow', {
					name: this.upload_options.workflow,
					lightweight: true
				});

				return true;
			} catch (error) {
				this.handleStepError(content, error);

				return false;
			}
		},
		async assignUserRole(content_id, content) {
			const contributor = this.upload_options.contributors[0];

			try {
				await PtApiClient.updateDraftWorkflow(content_id, 'method', 'reassignRole', {
					role: contributor.role,
					user_id: contributor.user_id,
					lightweight: true
				});

				return true;
			} catch (error) {
				this.handleStepError(content, error);

				return false;
			}
		},
		async updateRevisionBody(content_id, content) {
			try {
				await PtApiClient.updateDraftBody(content_id, content.body);

				return true;
			} catch (error) {
				this.handleStepError(content, error);

				return false;
			}
		},
		async updateRevisionMetadata(content_id, content) {
			const metadata = {
				library_id: content.uploaded.itemRevisionDraft?.header?.metadata?.library_id,
				custom_revision: content.custom_revision,
				title: content.title,
				content_id,
				summary_changes: '<p>Content created from bulk upload feature.</p>'
			};

			try {
				await PtApiClient.updateDraftMetadata(content_id, metadata);

				return true;
			} catch (error) {
				this.handleStepError(content, error);

				return false;
			}
		},
		async approveRevision(content_id, content) {
			try {
				await PtApiClient.updateDraftWorkflow(content_id, 'transition', 'approveMinorChange');

				return true;
			} catch (error) {
				this.handleStepError(content, error);

				return false;
			}
		},
		handleStepError(content, error) {
			console.error(error);
			content.status = ContentOperationStatus.error;
			content.error = error?.response?.data?.message || error.message;
		},
		getContentIdFrom(content) {
			return content.itemRevisionDraft.header.metadata.content_id;
		},
		close() {
			this.$emit('input', false);

			if (this.new_contents.length) this.refreshFolder(this.folder._id);

			this.new_contents = [];
		}
	},
	watch: {
		value() {
			this.state = BulkOperationState.waitingForFiles;
		},
		new_contents: {
			deep: true,
			handler() {
				if (this.new_contents.length === 0) {
					this.state = BulkOperationState.waitingForFiles;
				}
			}
		}
	}
};
</script>
